import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Progress,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { SubscriptionWorkflowType, useGetFundraisingQuery } from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency, formatNumberPercentage } from '@blockpulse3/helpers';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

type Props = unknown;

/**
 * FundraisingInformations.
 * Information section, such as percentage, amount... of the Fundraising.
 *
 * @returns {JSX.Element}
 */
export function FundraisingInformations(): JSX.Element {
  const i18nFundraisingInformations = useTranslations('FundraisingInformations');

  const { fundraisingId = '' } = useParams();

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
  });

  if (loading || !fundraisingId) {
    return (
      <Skeleton>
        <Card h="260px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="260px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="260px" />;
  }

  const fundraising = data.fundraising;
  const isAllInSubscriptionWorkflow =
    fundraising.subscriptionWorkflowType === SubscriptionWorkflowType.ALL_IN;

  const objective = fundraising.data?.objective || 0;
  const totalApprovedAmount = fundraising.totalApprovedAmount || 0;
  const totalDeclaredAmount = fundraising.totalDeclaredAmount || 0;
  const committedAmount = fundraising?.committedAmount || 0;

  return (
    <Card>
      <CardHeader>
        <Heading size="md">{i18nFundraisingInformations('GlobalInformation')}</Heading>
      </CardHeader>
      <CardBody>
        <Stack spacing="4">
          <Progress colorScheme="green" rounded="md" value={objective} />
          <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: '4', md: '8' }}>
            <Box bg="gray.50" color="white" p="4" w="full">
              <Text color="gray.500" fontSize="md" fontWeight="500">
                {i18nFundraisingInformations('SubscriptionAmount')}
              </Text>
              <Text color="gray.800" fontSize="lg" fontWeight="700">
                {formatNumberCurrency(committedAmount)}
              </Text>
              {isAllInSubscriptionWorkflow && totalDeclaredAmount && (
                <Text color="gray.500" fontSize="sm">
                  <Text as="span" fontWeight="500">
                    {i18nFundraisingInformations('Declared')}
                  </Text>{' '}
                  {formatNumberCurrency(totalDeclaredAmount)}
                </Text>
              )}
            </Box>
            <Box bg="gray.50" color="white" p="4" w="full">
              <Text color="gray.500" fontSize="md" fontWeight="500">
                {i18nFundraisingInformations('ProgressionPercentage')}
              </Text>
              <Text color="gray.800" fontSize="lg" fontWeight="700">
                {formatNumberPercentage(objective)}
              </Text>
            </Box>
            <Box bg="gray.50" color="white" p="4" w="full">
              <Text color="gray.500" fontSize="md" fontWeight="500">
                {isAllInSubscriptionWorkflow
                  ? i18nFundraisingInformations('ValidatedAmount')
                  : i18nFundraisingInformations('CollectedAmount')}
              </Text>
              <Text color="gray.800" fontSize="lg" fontWeight="700">
                {formatNumberCurrency(totalApprovedAmount)}
              </Text>
            </Box>
            <Box bg="gray.50" color="white" p="4" w="full">
              <Text color="gray.500" fontSize="md" fontWeight="500">
                {i18nFundraisingInformations('GuestInvestors', {
                  nb: fundraising.subscriptionCount,
                })}
              </Text>
              <Text color="gray.800" fontSize="lg" fontWeight="700">
                {fundraising.subscriptionCount}
              </Text>
            </Box>
          </Stack>
        </Stack>
      </CardBody>
    </Card>
  );
}

export type FundraisingInformationsProps = Props;
